import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useCookies } from '../contexts/CookieContext';

const { FiArrowLeft, FiCookie, FiSettings, FiCalendar, FiShield, FiInfo } = FiIcons;

const CookiePolicy = () => {
  const navigate = useNavigate();
  const { setShowCookieBanner } = useCookies();

  const handleManageCookies = () => {
    setShowCookieBanner(true);
  };

  const cookieCategories = [
    {
      id: 'necessary',
      name: 'Nødvendige cookies',
      description: 'Disse cookies er essensielle for at nettsiden skal fungere korrekt og kan ikke deaktiveres.',
      duration: 'Sesjon - 1 år',
      examples: [
        {
          name: 'auth-token',
          purpose: 'Lagrer innloggingsstatus',
          duration: '30 dager'
        },
        {
          name: 'session-id',
          purpose: 'Identifiserer brukersesjonen',
          duration: 'Sesjon'
        },
        {
          name: 'csrf-token',
          purpose: 'Beskytter mot sikkerhetstrusler',
          duration: 'Sesjon'
        }
      ],
      color: 'red'
    },
    {
      id: 'functional',
      name: 'Funksjonelle cookies',
      description: 'Disse cookies forbedrer funksjonaliteten og personaliseringen av nettsiden.',
      duration: '30 dager - 1 år',
      examples: [
        {
          name: 'language-pref',
          purpose: 'Husker språkvalg',
          duration: '1 år'
        },
        {
          name: 'theme-setting',
          purpose: 'Lagrer designpreferanser',
          duration: '6 måneder'
        },
        {
          name: 'form-data',
          purpose: 'Midlertidig lagring av skjemadata',
          duration: '1 time'
        }
      ],
      color: 'blue'
    },
    {
      id: 'analytics',
      name: 'Analyse cookies',
      description: 'Disse cookies hjelper oss å forstå hvordan nettsiden brukes og forbedre den.',
      duration: '2 år',
      examples: [
        {
          name: '_ga',
          purpose: 'Google Analytics - brukeridentifikasjon',
          duration: '2 år'
        },
        {
          name: '_ga_*',
          purpose: 'Google Analytics - sesjonsinformasjon',
          duration: '2 år'
        },
        {
          name: 'usage-stats',
          purpose: 'Intern bruksstatistikk',
          duration: '1 år'
        }
      ],
      color: 'green'
    },
    {
      id: 'marketing',
      name: 'Markedsføring cookies',
      description: 'Disse cookies brukes til å vise relevante annonser og markedsføringsinnhold.',
      duration: '30 dager - 2 år',
      examples: [
        {
          name: 'ad-preferences',
          purpose: 'Lagrer annonsepreferanser',
          duration: '1 år'
        },
        {
          name: 'social-tracking',
          purpose: 'Sporing fra sosiale medier',
          duration: '6 måneder'
        },
        {
          name: 'campaign-source',
          purpose: 'Sporer markedsføringskampanjer',
          duration: '30 dager'
        }
      ],
      color: 'purple'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      red: 'border-red-500 bg-red-50 text-red-900',
      blue: 'border-blue-500 bg-blue-50 text-blue-900',
      green: 'border-green-500 bg-green-50 text-green-900',
      purple: 'border-purple-500 bg-purple-50 text-purple-900'
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
            >
              <SafeIcon icon={FiArrowLeft} className="text-gray-600" />
            </button>
            <div className="flex items-center gap-3">
              <SafeIcon icon={FiCookie} className="text-blue-600 text-2xl" />
              <h1 className="text-3xl font-bold text-gray-900">Informasjonskapsler (Cookies)</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-lg overflow-hidden"
        >
          <div className="p-8 space-y-8">
            {/* Introduction */}
            <section>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                <h2 className="text-xl font-semibold text-blue-900 mb-3">Hva er informasjonskapsler?</h2>
                <p className="text-blue-800 leading-relaxed mb-4">
                  Informasjonskapsler (cookies) er små tekstfiler som lagres på din enhet når du besøker 
                  vår nettside. De hjelper oss å forbedre din opplevelse ved å huske dine preferanser og 
                  gi oss innsikt i hvordan nettsiden brukes.
                </p>
                <div className="flex items-center gap-4">
                  <button
                    onClick={handleManageCookies}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                  >
                    <SafeIcon icon={FiSettings} />
                    Administrer cookies
                  </button>
                  <div className="text-sm text-blue-700">
                    <SafeIcon icon={FiCalendar} className="inline mr-1" />
                    Sist oppdatert: 25. mai 2024
                  </div>
                </div>
              </div>
            </section>

            {/* Cookie Categories */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">Typer cookies vi bruker</h2>
              <div className="space-y-6">
                {cookieCategories.map((category) => (
                  <div key={category.id} className={`border-l-4 ${getColorClasses(category.color)} rounded-r-lg p-6`}>
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-xl font-semibold mb-2">{category.name}</h3>
                        <p className="text-sm opacity-90 mb-2">{category.description}</p>
                        <div className="text-xs opacity-75">
                          <strong>Typisk varighet:</strong> {category.duration}
                        </div>
                      </div>
                      {category.id === 'necessary' && (
                        <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-medium">
                          Påkrevd
                        </span>
                      )}
                    </div>

                    <div className="space-y-3">
                      <h4 className="font-medium text-sm opacity-90">Eksempler på cookies:</h4>
                      <div className="grid gap-3">
                        {category.examples.map((example, index) => (
                          <div key={index} className="bg-white bg-opacity-50 rounded-lg p-3">
                            <div className="flex justify-between items-start mb-1">
                              <span className="font-mono text-sm font-medium">{example.name}</span>
                              <span className="text-xs opacity-75">{example.duration}</span>
                            </div>
                            <p className="text-xs opacity-80">{example.purpose}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Managing Cookies */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Administrer dine cookieinnstillinger</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="border border-gray-200 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-3">På vår nettside</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Du kan når som helst endre dine cookiepreferanser ved å klikke på knappen nedenfor.
                  </p>
                  <button
                    onClick={handleManageCookies}
                    className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                  >
                    <SafeIcon icon={FiSettings} />
                    Endre cookieinnstillinger
                  </button>
                </div>

                <div className="border border-gray-200 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-3">I nettleseren din</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Du kan også blokkere eller slette cookies direkte i nettleserens innstillinger.
                  </p>
                  <div className="space-y-2 text-xs text-gray-500">
                    <div>
                      <strong>Chrome:</strong> Innstillinger → Personvern og sikkerhet → Cookies
                    </div>
                    <div>
                      <strong>Firefox:</strong> Innstillinger → Personvern og sikkerhet
                    </div>
                    <div>
                      <strong>Safari:</strong> Innstillinger → Personvern → Cookies
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Contact */}
            <section className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h2 className="text-xl font-semibold text-blue-900 mb-4">Spørsmål om cookies?</h2>
              <div className="space-y-2 text-blue-800">
                <p>Hvis du har spørsmål om vår bruk av cookies, kan du kontakte oss:</p>
                <div className="text-sm space-y-1">
                  <p><strong>E-post:</strong> personvern@hmsvarsling.no</p>
                  <p><strong>Telefon:</strong> 40 55 19 89</p>
                  <p><strong>Adresse:</strong> Employer Brand AS, Motzfeldts gate 21, 0561 Oslo</p>
                </div>
              </div>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CookiePolicy;